import React, { useState, useRef, useId, useLayoutEffect } from "react";
import { Grid, Paper, Typography, Box } from "@mui/material";
import {
  Close,
  ChildCare,
  Accessible,
  Report,
  Restaurant,
  Person,
  ConnectingAirports,
  FlightLand,
  FlightTakeoff,
  Luggage,
  EventSeat,
  Info,
} from "@mui/icons-material";
import { PassengerModal } from "../../components";
import {
  isOtherInfoCode,
  planSeatIcons,
} from "../../helpers/passengerServiceNormalizer";
import {
  getRewardTierBadge,
  getRewardTierIconUrl,
  getSeatBorderColor,
  getSeatHighlightColor,
} from "../../helpers/passengerIconHelpers";
import {
  buildLargeGroups,
  getGroupColor,
  getPassengerGroup,
  groupOutlinePaths,
  seatId,
} from "../../helpers/largeGroups";

const AISLE_COLUMN_WIDTH = 48;
const ROW_GAP_BLEED = 4;

const renderRewardMark = (passenger, reward, size) => {
  if (!reward) return null;

  const tierCoinUrl = reward.iconKey ? getRewardTierIconUrl(reward) : null;
  const tierBadge = !tierCoinUrl ? getRewardTierBadge(reward) : null;

  if (tierCoinUrl) {
    return (
      <img
        src={tierCoinUrl}
        alt={reward.tierDisplayName || ""}
        style={{ width: size, height: size, objectFit: "contain" }}
      />
    );
  }

  if (!tierBadge) return null;

    return (
      <Box
        sx={{
        width: size,
        height: size,
          borderRadius: "50%",
          backgroundColor: tierBadge.bg,
          border: "1px solid rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      title={reward.tierDisplayName || ""}
      >
        {passenger.isInfant ? (
        <ChildCare sx={{ fontSize: Math.max(size - 7, 10), color: tierBadge.fg }} />
        ) : (
        <Person sx={{ fontSize: Math.max(size - 7, 10), color: tierBadge.fg }} />
        )}
      </Box>
    );
};

const renderSeatPassengerIcon = (passenger, classification, rewardInPrimary) => {
  if (passenger.passengerType === "B") {
    return <Luggage sx={{ fontSize: 20 }} />;
  }
  if (passenger.passengerType === "E") {
    return <EventSeat sx={{ fontSize: 20 }} />;
  }

  if (rewardInPrimary) {
    const rewardMark = renderRewardMark(
      passenger,
      classification.reward,
      classification.reward?.iconKey ? 28 : 22,
    );
    if (rewardMark) return rewardMark;
  }

  if (passenger.isInfant) {
    return <ChildCare sx={{ fontSize: 20 }} />;
  }

  return <Person sx={{ fontSize: 20 }} />;
};

export const SeatDetails = ({
  seatingDetails,
  handleModalOpen,
  selectedFilter,
  filteredPassengerList,
  actualPassengers,
  mealModeOn,
  isRotated,
  compressView = false,
  getPassengerBagInfo = () => null,
}) => {
  const [longPressedSeat, setLongPressedSeat] = useState(null);
  const [isPassengerModalOpen, setIsPassangerModalOpen] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [ringPaths, setRingPaths] = useState([]);
  const overlayParentRef = useRef(null);
  const clipIdPrefix = `large-group-${useId().replace(/[^\w-]/g, "")}`;

  const specialServices = ["BLND", "DEAF", "MEDA", "MAAS", "UMNR"];
  const generalServices = [
    "DPNA",
    "DEPA",
    "DEPU",
    "INAD",
    "ESAN",
    "SVAN",
    "PICA",
    "PICU",
    "AOXY",
    "POXY",
    "ALGC",
    "WEAP",
    "AVIH",
    "PETC",
    "CBBG",
    "EXST",
  ];
  const accessibleServices = ["WCHR", "WCHS", "WCHC"];
  const connectingFlight = ["connectingFlight"];

  const leftSideColumns = ["A", "B", "C"];
  const rightSideColumns = ["D", "E", "F"];

  const handleLongPress = (passenger) => {
    if (passenger) {
      setLongPressedSeat(passenger.id);
      const passengerWithBags = {
        ...passenger,
        bagInfo: getPassengerBagInfo(passenger),
      };
      setSelectedPassenger(passengerWithBags);
      setIsPassangerModalOpen(true);

      // Reset after delay
      setTimeout(() => setLongPressedSeat(null), 500);
    }
  };

  // Close the detailed modal
  const handleCloseDetailModal = () => {
    setIsPassangerModalOpen(false);
    setSelectedPassenger(null);
  };

  const filterSeatingDetails = (passengersList, seatingDetailsList) => {
    const passengerIdsSet = new Set(passengersList.map((p) => p.id));
    return seatingDetailsList.filter(
      (s) => s.passenger && passengerIdsSet.has(s.passenger.id),
    );
  };

  const filteredSeatingDetailsMemo = filterSeatingDetails(
    filteredPassengerList,
    seatingDetails,
  );

  const groupedByZone = seatingDetails.reduce((acc, seat) => {
    const { zone } = seat;
    if (!acc[zone]) acc[zone] = [];
    acc[zone].push(seat);
    return acc;
  }, {});

  const sortedZones = Object.keys(groupedByZone)
    .sort()
    .map((zone) => ({
      zone,
      seats: groupedByZone[zone].sort((a, b) => {
        if (parseInt(a.row) === parseInt(b.row)) {
          return a.coordinates.x - b.coordinates.x;
        }
        return parseInt(a.row) - parseInt(b.row);
      }),
    }));

  const passengerHasSeat = (passenger) =>
    passenger &&
    filteredSeatingDetailsMemo.some((d) => d.passengerId === passenger.id);

  useLayoutEffect(() => {
    const container = overlayParentRef.current;

    if (mealModeOn || !container || !selectedFilter?.LARGE_GROUPS) {
      setRingPaths([]);
      return undefined;
    }

    const measure = () => {
      const groups = buildLargeGroups(actualPassengers);
      const largeKeys = new Set(groups.map(({ key }) => key));
      const seatsById = new Map(
        seatingDetails.map((seat) => [seatId(seat.row, seat.column), seat])
      );

      const seats = [...container.querySelectorAll("[data-seat-id]")].map(
        (el) => {
          const seat = seatsById.get(el.dataset.seatId);
          const group = getPassengerGroup(seat?.passenger);
          let left = 0;
          let top = 0;
          for (let node = el; node && node !== container; node = node.offsetParent) {
            left += node.offsetLeft;
            top += node.offsetTop;
          }
          return {
            column: seat?.column,
            groupKey: group && largeKeys.has(group.key) ? group.key : null,
            left,
            top,
            width: el.offsetWidth,
            height: el.offsetHeight,
          };
        }
      );

      const pulseKey =
        selectedFilter.selectedGroup ||
        (groups.length === 1 ? groups[0].key : null);

      setRingPaths(
        groupOutlinePaths(seats).map(({ groupKey, d }) => ({
          groupKey,
          d,
          color: getGroupColor(groupKey, groups),
          selected: pulseKey === groupKey,
        }))
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [
    actualPassengers,
    compressView,
    mealModeOn,
    seatingDetails,
    selectedFilter,
  ]);

  const hasSelectedRing = ringPaths.some((ring) => ring.selected);

  const getSeatColor = (passenger) => {
    return getSeatHighlightColor(
      passenger,
      selectedFilter,
      mealModeOn,
      passengerHasSeat(passenger),
    );
  };

  const getBorderColor = (passenger) => {
    return getSeatBorderColor(
      passenger,
      mealModeOn,
      passengerHasSeat(passenger),
    );
  };

  const getServiceIcon = (service) => {
    const code =
      typeof service === "string"
        ? service
        : typeof service?.code === "string"
        ? service.code
        : "";
    if (!code) return null;

    if (code.endsWith("ML") || code === "MEAL") {
      if (mealModeOn) {
        const category = typeof service === "object" ? service.category : "";
        const isSpecial = category === "Special";

        // For Special meals, show both category and code
        // For Adult, Child, Upgraded - show only category
        return (
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", fontSize: 10, display: "block" }}
            >
              {category}
            </Typography>
            {isSpecial && (
              <Typography
                variant="caption"
                sx={{ fontSize: 9, display: "block" }}
              >
                {code}
              </Typography>
            )}
          </Box>
        );
      } else {
        return <Restaurant sx={{ fontSize: 12 }} />;
      }
    }

    if (!mealModeOn) {
      const upper = String(code).toUpperCase();
      // Treat ANY WC* code as wheelchair
      if (accessibleServices.includes(code) || upper.startsWith("WC")) {
        return <Accessible sx={{ fontSize: 16 }} />;
      }

      if (specialServices.includes(code) || generalServices.includes(code)) {
        return <Report sx={{ fontSize: 16, color: "#003251" }} />;
      }

      // [i] Info icon for OTHER_INFO codes
      if (isOtherInfoCode(code)) {
        return <Info sx={{ fontSize: 16, color: "#1976d2" }} />;
      }

      if (connectingFlight.includes(code)) {
        return <ConnectingAirports sx={{ fontSize: 16, color: "#000" }} />;
      }
    }

    return null;
  };

  // Ensure only a single wheelchair icon is rendered even if multiple WC* services exist
  const filterServicesForIcons = (services) => {
    let wheelchairRendered = false;
    return (services || []).filter((service) => {
      const code =
        typeof service === "string"
          ? service
          : typeof service?.code === "string"
          ? service.code
          : "";
      const upper = String(code).toUpperCase();
      if (upper.startsWith("WC")) {
        if (wheelchairRendered) return false;
        wheelchairRendered = true;
        return true;
      }
      return true;
    });
  };

  const getConnectionIcons = (passenger) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        {passenger.inboundConnections &&
          passenger.inboundConnections.length > 0 && (
            <FlightLand sx={{ fontSize: 12 }} />
          )}
        {passenger.outboundConnections &&
          passenger.outboundConnections.length > 0 && (
            <FlightTakeoff sx={{ fontSize: 12 }} />
          )}
      </Box>
    );
  };

  const getSeatDisplay = (passenger) => {
    if (!passengerHasSeat(passenger)) {
      return <Close fontSize="small" style={{ fill: "#E3E3E3" }} />;
    }

    // In meal mode, show person/child icon and meal information
    if (mealModeOn) {
      const mealInfo = passenger.mealObject;
      const category = typeof mealInfo === "object" ? mealInfo.category : "";
      const code = typeof mealInfo === "object" ? mealInfo.code : mealInfo;
      const isStaffLabel = passenger.isStaff ? "ID" : "";
      const isSpecial = category === "Special";

      return (
        <Box
          position="relative"
          width={50}
          height={50}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            fontSize={8}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              padding: "2px",
            }}
          >
            {passenger.passengerType}
          </Typography>
          <Typography
            fontSize={8}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              padding: "2px",
            }}
          >
            {isStaffLabel}
          </Typography>
          {passenger.isInfant ? (
            <ChildCare sx={{ fontSize: 18 }} />
          ) : (
            <Person sx={{ fontSize: 18 }} />
          )}
          {mealInfo && (
            <Box sx={{ textAlign: "center", mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  fontSize: 9,
                  display: "block",
                  lineHeight: 1.5,
                }}
              >
                {category}
              </Typography>
              {isSpecial && (
                <Typography
                  variant="caption"
                  sx={{ fontSize: 8, display: "block", lineHeight: 1 }}
                >
                  {code}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    }

    const servicesAndMeals = [
      ...(passenger.services || []),
      ...(passenger.mealObject
        ? [
            typeof passenger.mealObject === "string"
              ? passenger.mealObject
              : passenger.mealObject.code,
          ]
        : []),
      ...(passenger.connectingFlight ? ["connectingFlight"] : []),
    ];

    const isStaffLabel = passenger.isStaff ? "ID" : "";

    const iconPlan = planSeatIcons(
      selectedFilter,
      passenger,
      filterServicesForIcons(servicesAndMeals),
    );
    const classification = iconPlan.classification;

    return (
      <Box position="relative" width={50} height={50}>
        <Typography
          fontSize={8}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            padding: "2px",
            lineHeight: 1,
          }}
        >
          {passenger.passengerType}
        </Typography>
        <Typography
          fontSize={8}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            padding: "2px",
          }}
        >
          {isStaffLabel}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            pt: "7px",
            pb: "9px",
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <Box sx={{ display: "flex", flexShrink: 0, lineHeight: 0 }}>
            {renderSeatPassengerIcon(
              passenger,
              classification,
              iconPlan.rewardInPrimary,
            )}
          </Box>
              <Box
                sx={{
                  display: "flex",
              flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
              flexShrink: 0,
              mt: "1px",
              lineHeight: 0,
                }}
              >
            {iconPlan.rowServices.map((service, index) => {
                  const IconComponent = getServiceIcon(service);
                  return (
                <Box key={index} sx={{ display: "flex", lineHeight: 0 }}>
                      {IconComponent}
                </Box>
                  );
            })}
              {getConnectionIcons(passenger)}
          </Box>
        </Box>
      </Box>
    );
  };

  const Seat = ({ seat, handleModalOpen, isRotated }) => {
    const isLongPressed = longPressedSeat === seat.passenger?.id;
    const longPressTimer = useRef(null);
    const longPressFired = useRef(false);
    const clickDelayTimer = useRef(null);
    const longPressThreshold = 500;
    const [hasStartedLongPress, setHasStartedLongPress] = useState(false);

    // Track touch start position to calculate distance moved
    const touchStartPos = useRef({ x: 0, y: 0 });
    const maxDistanceMoved = useRef(0);
    const touchStartTime = useRef(0);

    // Distance threshold in pixels - if finger moves more than this, it's a scroll
    const SCROLL_THRESHOLD_PX = 15;

    const handlePressStart = (e) => {
      if (!seat.passenger) return;

      // Get starting position from touch or mouse event
      const clientX = e.touches?.[0]?.clientX ?? e.clientX;
      const clientY = e.touches?.[0]?.clientY ?? e.clientY;

      touchStartPos.current = { x: clientX, y: clientY };
      maxDistanceMoved.current = 0;
      touchStartTime.current = Date.now();
      setHasStartedLongPress(true);
      longPressFired.current = false;

      if (mealModeOn) {
        longPressTimer.current = setTimeout(() => {
          // Only fire long press if we haven't scrolled
          if (maxDistanceMoved.current < SCROLL_THRESHOLD_PX) {
            handleLongPress(seat.passenger);
            longPressFired.current = true;
          }
          longPressTimer.current = null;
        }, longPressThreshold);
      }
    };

    const handleMove = (e) => {
      if (!hasStartedLongPress) return;

      // Calculate distance from start position
      const clientX = e.touches?.[0]?.clientX ?? e.clientX;
      const clientY = e.touches?.[0]?.clientY ?? e.clientY;

      const deltaX = clientX - touchStartPos.current.x;
      const deltaY = clientY - touchStartPos.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Track the maximum distance moved during this touch
      maxDistanceMoved.current = Math.max(maxDistanceMoved.current, distance);

      // If we've moved beyond threshold, cancel long-press timer
      if (maxDistanceMoved.current >= SCROLL_THRESHOLD_PX) {
        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
      }
    };

    const handlePressEnd = (e) => {
      // Always clear any pending long-press timer
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }

      // If finger moved too far, it was a scroll - don't trigger click
      const wasScroll = maxDistanceMoved.current >= SCROLL_THRESHOLD_PX;

      if (wasScroll) {
        setHasStartedLongPress(false);
        maxDistanceMoved.current = 0;
        return;
      }

      // Trigger regular click if long-press did NOT fire
      if (seat.passenger && !longPressFired.current) {
        clickDelayTimer.current = setTimeout(() => {
          if (mealModeOn && seat.passenger.mealObject) {
            handleModalOpen(seat.passenger);
          } else if (!mealModeOn) {
            handleModalOpen(seat.passenger);
          }
        }, 50);
      }

      setHasStartedLongPress(false);
      maxDistanceMoved.current = 0;
    };

    const handlePressLeave = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      setHasStartedLongPress(false);
      maxDistanceMoved.current = 0;
    };

    return (
      <Paper
        data-seat-id={seatId(seat.row, seat.column)}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseMove={handleMove}
        onMouseLeave={handlePressLeave}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchMove={handleMove}
        onTouchCancel={handlePressLeave}
        sx={{
          width: 50,
          height: 50,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: getSeatColor(seat.passenger),
          border: `1px solid ${getBorderColor(seat.passenger)}`,
          boxShadow: isLongPressed ? "0px 0px 8px 2px rgba(0,0,0,0.3)" : "none",
          cursor: seat.passenger ? "pointer" : "default",
          transition: "box-shadow 0.2s ease-in-out",
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
          // Prevent double-tap zoom and improve touch responsiveness
          touchAction: "manipulation",
          transform: isRotated ? "rotate(180deg)" : "none",
        }}
      >
        {getSeatDisplay(seat.passenger)}
      </Paper>
    );
  };

  const SeatRow = ({ seats, handleModalOpen, isRotated }) => {
    const characteristicsSet = new Set(
      seats.flatMap((seat) => seat.seatCharacteristics || []),
    );
    const hasExit = characteristicsSet.has("E");

    const leftSideSeats = seats.filter((seat) =>
      leftSideColumns.includes(seat.column),
    );
    const rightSideSeats = seats.filter((seat) =>
      rightSideColumns.includes(seat.column),
    );

    return (
      <Box sx={{ position: "relative", width: "100%" }}>

        {hasExit && (
          <Grid
            container
            item
            alignItems="center"
            sx={{ marginTop: 1, position: "relative", zIndex: 1 }}
          >
            <Grid item xs={5} textAlign="start">
              <Box
                sx={{
                  display: "inline-block",
                  paddingX: "4px",
                  borderRadius: "4px",
                  marginLeft: "80px",
                  transform: isRotated ? "rotate(180deg)" : "none",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "bold",
                    transform: isRotated ? "rotate(180deg)" : "none",
                  }}
                >
                  EXIT
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={2} />

            <Grid item xs={5} textAlign="end">
              <Box
                sx={{
                  display: "inline-block",
                  paddingX: "4px",
                  borderRadius: "4px",
                  marginRight: "90px",
                  transform: isRotated ? "rotate(180deg)" : "none",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "bold",
                    transform: isRotated ? "rotate(180deg)" : "none",
                  }}
                >
                  EXIT
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}

        {hasExit && (
          <Box sx={{ height: 16, position: "relative", zIndex: 1 }} />
        )}

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexWrap: "nowrap",
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Seats left side */}
          <Box
            sx={{
              flex: "5 1 0",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: 0,
              minWidth: 0,
            }}
          >
            {leftSideSeats.map((seat, index) => (
              <Box key={index} sx={{ m: "1px", flexShrink: 0 }}>
                <Seat
                  seat={seat}
                  handleModalOpen={handleModalOpen}
                  isRotated={isRotated}
                />
              </Box>
            ))}
          </Box>

          {/* Row number */}
          <Box
            sx={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: AISLE_COLUMN_WIDTH,
              minWidth: AISLE_COLUMN_WIDTH,
              maxWidth: AISLE_COLUMN_WIDTH,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                transform: isRotated ? "rotate(180deg)" : "none",
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                minWidth: AISLE_COLUMN_WIDTH,
                textAlign: "center",
              }}
            >
              {seats[0].row}
            </Typography>
          </Box>

          {/* Seats right side */}
          <Box
            sx={{
              flex: "5 1 0",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              p: 0,
              minWidth: 0,
            }}
          >
            {rightSideSeats.map((seat, index) => (
              <Box key={index} sx={{ m: "1px", flexShrink: 0 }}>
                <Seat
                  seat={seat}
                  handleModalOpen={handleModalOpen}
                  isRotated={isRotated}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {isPassengerModalOpen && (
        <Grid container style={{ zIndex: 999 }}>
          <PassengerModal
            isOpen={!!isPassengerModalOpen}
            handleClose={handleCloseDetailModal}
            selectedPassenger={selectedPassenger}
            bagInfo={
              selectedPassenger?.bagInfo ||
              getPassengerBagInfo(selectedPassenger) ||
              null
            }
          />
        </Grid>
      )}

      <Box
        ref={overlayParentRef}
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          marginTop: 2,
          transition: "transform 0.6s ease-in-out",
          transform: isRotated ? "rotate(180deg)" : "rotate(0deg)",
          transformOrigin: "center center",
          overflowX: "visible",
          "@keyframes largeGroupRipple": {
            from: { strokeWidth: "2px", strokeOpacity: 0.5 },
            to: { strokeWidth: "22px", strokeOpacity: 0 },
          },
          "@keyframes largeGroupFadeIn": {
            from: { opacity: 0 },
          },
          "& .large-group-ring": {
            animation: "largeGroupFadeIn 400ms ease-out",
            transition: "opacity 300ms ease",
          },
          "& .large-group-ripple": {
            animation: "largeGroupRipple 2.8s ease-out infinite",
          },
          "@media (prefers-reduced-motion: reduce)": {
            "& .large-group-ring": { animation: "none" },
            "& .large-group-ripple": {
              animation: "none",
              strokeWidth: "10px",
              strokeOpacity: 0.1,
            },
          },
        }}
      >
        <Grid container direction="column" spacing={0} sx={{ rowGap: 0.5 }}>
              {(() => {
            const allRows = sortedZones.flatMap(({ seats }) => {
                const rowsObject = seats.reduce((rows, seat) => {
                  if (!rows[seat.row]) rows[seat.row] = [];
                  rows[seat.row].push(seat);
                  return rows;
                }, {});
              return Object.keys(rowsObject)
                .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
                  .map((rowNumber) => rowsObject[rowNumber]);

            });

            allRows.sort(
              (a, b) => parseInt(a[0].row, 10) - parseInt(b[0].row, 10),
            );

                const visibleRows = compressView
              ? allRows.filter((seatsInRow) =>
                      seatsInRow.some(
                        (seat) =>
                      seat.passenger && passengerHasSeat(seat.passenger),
                  ),
                      )
              : allRows;

            const rowIsOverWing = (seatsInRow) => {
              const set = new Set(
                seatsInRow.flatMap((seat) => seat.seatCharacteristics || []),
              );
              return set.has("OW");
            };

            const shouldShowZoneGap = (prevRowSeats, nextRowSeats) => {
              if (!prevRowSeats?.length || !nextRowSeats?.length) {
                return false;
              }
              const prevZone = prevRowSeats[0].zone;
              const nextZone = nextRowSeats[0].zone;
              if (prevZone === nextZone) {
                return false;
              }
              const prevRow = parseInt(prevRowSeats[0].row, 10);
              const nextRow = parseInt(nextRowSeats[0].row, 10);
              if (
                rowIsOverWing(prevRowSeats) &&
                rowIsOverWing(nextRowSeats) &&
                nextRow === prevRow + 1
              ) {
                return false;
              }
              return true;
            };

            const rowBlocks = [];
            let i = 0;
            while (i < visibleRows.length) {
              const seatsInRow = visibleRows[i];
              const isOW = rowIsOverWing(seatsInRow);

              if (!isOW) {
                rowBlocks.push({ type: "single", rows: [seatsInRow] });
                i += 1;
                continue;
              }

              let j = i + 1;
              while (
                j < visibleRows.length &&
                rowIsOverWing(visibleRows[j])
              ) {
                j += 1;
              }

              rowBlocks.push({
                type: "ow",
                rows: visibleRows.slice(i, j),
              });
              i = j;
            }

            const renderedRows = [];
            rowBlocks.forEach((block, blockIndex) => {
              if (blockIndex > 0) {
                const prevBlock = rowBlocks[blockIndex - 1];
                const prevLastRow = prevBlock.rows[prevBlock.rows.length - 1];
                const nextFirstRow = block.rows[0];
                if (shouldShowZoneGap(prevLastRow, nextFirstRow)) {
                  renderedRows.push(
                    <Box key={`zone-gap-${blockIndex}`} sx={{ height: 40 }} />,
                  );
                }
              }

              if (block.type === "single") {
                renderedRows.push(
                  <SeatRow
                    key={`row-${blockIndex}`}
                    seats={block.rows[0]}
                    handleModalOpen={handleModalOpen}
                    isRotated={isRotated}
                  />,
                );
                return;
              }

              renderedRows.push(
                <Box
                  key={`ow-${blockIndex}`}
                  sx={{
                    position: "relative",
                    width: "100%",
                    overflow: "visible",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -ROW_GAP_BLEED,
                      bottom: -ROW_GAP_BLEED,
                      left: -140,
                      right: -140,
                      background:
                        "linear-gradient(to right, transparent 0%, rgb(210, 210, 210) 6%, rgb(210, 210, 210) 94%, transparent 100%)",
                      opacity: 0.7,
                      pointerEvents: "none",
                      zIndex: 0,
                      borderRadius: 2,
                    }}
                  />
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {block.rows.map((groupRowSeats, k) => (
                  <SeatRow
                        key={`ow-row-${blockIndex}-${k}`}
                        seats={groupRowSeats}
                    handleModalOpen={handleModalOpen}
                    isRotated={isRotated}
                  />
                    ))}
                  </Box>
                </Box>,
              );
            });

            return renderedRows;
              })()}
        </Grid>
        {!mealModeOn && ringPaths.length > 0 && (
          <svg
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              overflow: "visible",
              zIndex: 3,
            }}
          >
            {ringPaths.map((ring, index) => (
              <g
                key={ring.groupKey}
                className="large-group-ring"
                fill="none"
                stroke={ring.color}
                strokeLinejoin="round"
                style={{
                  opacity: hasSelectedRing && !ring.selected ? 0.3 : 1,
                }}
              >
                {ring.selected && (
                  <>
                    <clipPath id={`${clipIdPrefix}-${index}`}>
                      <path
                        clipRule="evenodd"
                        d={`M -1e4 -1e4 H 1e5 V 1e5 H -1e4 Z ${ring.d}`}
                      />
                    </clipPath>
                    <g clipPath={`url(#${clipIdPrefix}-${index})`}>
                      <path className="large-group-ripple" d={ring.d} />
                      <path
                        className="large-group-ripple"
                        d={ring.d}
                        style={{ animationDelay: "-1.4s" }}
                      />
                    </g>
                  </>
                )}
                <path d={ring.d} strokeWidth={ring.selected ? 2.5 : 2} />
              </g>
            ))}
          </svg>
        )}
      </Box>
    </>
  );
};
